<svelte:head>
  <title>Request | {charity?.name || 'Charity'} | A Good Thing</title>
</svelte:head>

<div class="block is-size-7">
  <a href={$url('../../..')}> &lt; Back to offer</a>
</div>

{#if charity}
  {#if data.status === 'withdrawn'}
    <article class="message is-info">
      <div class="message-body">This charity withdrew its request.</div>
    </article>
  {/if}
  {#if data.status === 'unsuccessful'}
    <article class="message is-warning">
      <div class="message-body">Request declined</div>
    </article>
  {/if}
  <nav class="level">
    <!-- Left side -->
    <div class="level-left">
      <div class="level-item">
        <h2 class="title">{charity.name} <RequestStatusTag status={data.status || 'new'}/></h2>
      </div>
    </div>
    <div class="level-right">
      <div class="level-item">
        <button class="button" on:click={() => {
          $uiState.activeModal = { component: ConfirmComplete, props: { id: requestId } };
        }}>Mark as complete</button>&nbsp;
        <button class="button" on:click={() => {
          $uiState.activeModal = { component: ConfirmAbandoned, props: { id: requestId } };
        }}>Mark as abandoned</button>
      </div>
    </div>
  </nav>

  {#if data.created}
    <div class="block has-text-grey-light">
      Request made {dayjs().to(data.created)} |
      <a href={$url('../../../../charity/' + data.charity.id)}
        >View charity details</a>
    </div>
  {/if}

  {#if data.message && !isChatEnabled}
    <p class="block">
      <LargeMessage>{data.message}</LargeMessage>
    </p>
  {/if}

  {#if !isChatEnabled}
    <hr />
  {/if}

  {#if isChatEnabled}
    <div class="tabs">
      <ul>
        <li
          class:is-active={!showMessages}
          on:click={() => {
            if (showMessages) {
              showMessages = false;
            }
          }}>
          <!-- svelte-ignore a11y-missing-attribute -->
          <a> Charity Profile </a>
        </li>
        {#if data.messages && data.messages?.length > 0}
          <li
            class:is-active={showMessages}
            on:click={() => {
              if (!showMessages) {
                showMessages = true;
              }
            }}>
            <!-- svelte-ignore a11y-missing-attribute -->
            <a> Messages </a>
          </li>
        {/if}
      </ul>
    </div>
  {/if}

  {#if isChatEnabled && showMessages && data.messages}
    <Chat
      messageIds={data.messages ?? []}
      isAdmin
      companyName={company.name}
      charityName={charity.name} />
  {/if}

  {#if !showMessages || !isChatEnabled}
    {#if charity.type === 'EnglandAndWalesRegistered'}
      <EnglandWalesCharityFull {charity} />
    {:else if charity.type === 'ScotlandRegistered'}
      <ScotlandCharityFull {charity} />
    {:else if charity.type === 'NorthernIrelandRegistered'}
      <NorthernIrelandCharityFull {charity} />
    {:else if charity.type === 'CIC'}
      <CICFull {charity} />
    {:else if charity.type === 'CBS'}
      <CBSFull {charity} />
    {:else if charity.type === 'HMRCCharity'}
      <HMRCCharityFull {charity} />
    {:else}
      <UnregisteredCharityFull {charity} />
    {/if}
  {/if}
{/if}

<script lang="ts">
  export let requestId: string;
  import _ from 'lodash';
  import { uiState } from '../../../../../lib/uiState';
  import getFeatureFlags from '../../../../../utils/getFeatureFlags';
  import { params, url } from '@sveltech/routify';
  import EnglandWalesCharityFull from '../../../../_components/org_profiles/EnglandWalesCharityFull.svelte';
  import ScotlandCharityFull from '../../../../_components/org_profiles/ScotlandCharityFull.svelte';
  import NorthernIrelandCharityFull from '../../../../_components/org_profiles/NorthernIrelandCharityFull.svelte';
  import CICFull from '../../../../_components/org_profiles/CICFull.svelte';
  import CBSFull from '../../../../_components/org_profiles/CBSFull.svelte';
  import HMRCCharityFull from '../../../../_components/org_profiles/HMRCCharityFull.svelte';
  import UnregisteredCharityFull from '../../../../_components/org_profiles/UnregisteredCharityFull.svelte';
  import LargeMessage from '../../../../_components/LargeMessage.svelte';
  import dayjs from 'dayjs';
  import relativeTime from 'dayjs/plugin/relativeTime';
  import Chat from '../../../../_components/Chat.svelte';
  import RequestStatusTag from '../../../../_components/RequestStatusTag.svelte'
  import ConfirmComplete from './_components/ConfirmComplete.svelte';
  import ConfirmAbandoned from './_components/ConfirmAbandoned.svelte';

  dayjs.extend(relativeTime);
  const { isChatEnabled } = getFeatureFlags();

  let offerId: string = $params['id'];

  let data: Request_v2,
    charity: Charity_v2,
    offer: Offer_v2,
    company: Company_v1;
  let showMessages = false;
  let firebase = window.firebase;

  let messages: Message_v1[] = [];

  get();

  $: if (data) {
    getCharity(data.charity.id);
    getOffer(data.offer);
    getMessages();
  }

  $: if (offer) {
    getCompany(offer.company);
  }

  async function get() {
    firebase
      .firestore()
      .collection('requests_v2')
      .doc(requestId)
      .onSnapshot((doc) => {
        data = doc.data() as Request_v2;
      });
  }

  async function getCharity(id: string) {
    firebase
      .firestore()
      .collection('charities_v2')
      .doc(id)
      .onSnapshot((doc) => {
        charity = doc.data() as Charity_v2;
      });
  }

  async function getOffer(id: OfferId) {
    firebase
      .firestore()
      .collection('offers_v2')
      .doc(id)
      .onSnapshot((doc) => {
        offer = doc.data() as Offer_v2;
      });
  }

  async function getCompany(id: CompanyId) {
    firebase
      .firestore()
      .collection('companies_v1')
      .doc(id)
      .onSnapshot((doc) => {
        company = doc.data() as Company_v1;
      });
  }

  function getMessages() {
    if (data.messages) {
      data.messages.forEach((messageId) => {
        firebase
          .firestore()
          .collection('messages_v1')
          .doc(messageId)
          .onSnapshot((doc) => {
            messages.push(doc.data() as Message_v1);
          });
      });
    }
  }
</script>
